import { ChatAgentType } from '@constants/chat-agents';
import React, { useMemo } from 'react';
import type { ChatMessage } from '../../../../types/chat-agent';
import { SenderType } from '../../../../types/chat-message';
import { extractImageUrlsFromBlockContents } from '@utils/chat-message';

interface QuotedMessagePreviewProps {
  quotedMessage: ChatMessage;
  onCancel: () => void;
  activeAgent: ChatAgentType;
}

const extractMarkdownImageUrls = (text: string): string[] => {
  if (!text) return [];
  const urls: string[] = [];
  const imgRegex = /!\[image\]\((.*?)\)/g;
  let match;
  while ((match = imgRegex.exec(text)) !== null) {
    if (match[1]?.trim()) urls.push(match[1].trim());
  }
  return urls;
};

const QuotedMessagePreview: React.FC<QuotedMessagePreviewProps> = ({
  quotedMessage,
  onCancel,
}) => {
  const getSenderName = () => {
    if (quotedMessage.user === SenderType.USER) {
      return 'chính bạn';
    } else if (quotedMessage.agentType === ChatAgentType.CSKH) {
      return 'CSKH';
    } else {
      return 'Tendoo AI';
    }
  };

  const previewImageUrl = useMemo(() => {
    const fromUpload = quotedMessage.imageUploadItems
      ?.map((item) => item.s3Url || item.localUrl)
      .find((url) => Boolean(url?.trim()));
    if (fromUpload) return fromUpload;

    const fromBlocks = extractImageUrlsFromBlockContents(quotedMessage.blockContents);
    if (fromBlocks[0]) return fromBlocks[0];

    const payloadImage = quotedMessage.blockContents
      ?.map((block: any) => block.payload?.imageUrl as string | undefined)
      .find((url) => Boolean(url?.trim()));
    if (payloadImage) return payloadImage;

    const fromMarkdown = extractMarkdownImageUrls(quotedMessage.text || '');
    return fromMarkdown[0] || null;
  }, [quotedMessage]);

  const getPreviewText = () => {
    const text = quotedMessage.text || '';

    const hasOnlyImages = text.trim().match(/^(\!\[image\]\(.*?\)\s*)+$/);
    if (hasOnlyImages) {
      return 'Ảnh';
    }

    let textOnly = text
      .replace(/\!\[image\]\(.*?\)/g, '')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/#{1,6}\s+/g, '')
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (textOnly) return textOnly;
    if (previewImageUrl) return 'Ảnh';
    return quotedMessage.suggestAgentNotify || 'Ảnh';
  };

  return (
    <div className="quoted-message-preview mb-2 flex items-center justify-between gap-[10px] rounded-[14px_14px_2px_2px] bg-gray-100 p-2">
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <div className="font-[Roboto] text-sm font-normal leading-[18px] tracking-normal text-[#242428]">
          {"Đang trả lời "}
          {/* Cùng baseline với "Đang trả lời" (không dùng align-middle) */}
          <b className="font-semibold">{getSenderName()}</b>
        </div>
        <div
          className="line-clamp-3 break-words font-[Roboto] text-xs font-normal leading-[15px] text-[var(--colour-primary-neutral-shade-sn)]"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
          }}
        >
          {getPreviewText()}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {previewImageUrl && (
          <img
            src={previewImageUrl}
            alt=""
            className="h-10 w-10 rounded-lg object-cover opacity-100"
            width={40}
            height={40}
          />
        )}
        <button
          type="button"
          onClick={onCancel}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[21px] bg-white text-[#242428] opacity-100"
          aria-label="Hủy trích dẫn"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default QuotedMessagePreview;
